import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { reviews } from '../data/landing';
import { LogoMark } from './LogoMark';

// Card gap follows the page gutter (--gutter), read live from the track
const gapOf = (el: HTMLElement) => parseFloat(getComputedStyle(el).columnGap) || 0;
export function Reviews() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [current, setCurrent] = useState(1);
  const [progress, setProgress] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const first = track.firstElementChild as HTMLElement | null;
      if (!first) return;
      const step = first.offsetWidth + gapOf(track);
      const max = track.scrollWidth - track.clientWidth;
      const index = Math.round(track.scrollLeft / step) + 1;
      setCurrent(Math.min(Math.max(index, 1), reviews.length));
      setProgress(max > 0 ? track.scrollLeft / max : 0);
      setAtStart(track.scrollLeft <= 2);
      setAtEnd(track.scrollLeft >= max - 2);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  const go = (dir: 1 | -1) => {
    const track = trackRef.current;
    const first = track?.firstElementChild as HTMLElement | null;
    if (!track || !first) return;
    track.scrollBy({
      left: dir * (first.offsetWidth + gapOf(track)),
      behavior: 'smooth'
    });
  };

  // Click / drag on the progress rail to scrub through the reviews
  const scrubTo = (clientX: number, rail: HTMLElement) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = rail.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    track.scrollTo({
      left: ratio * (track.scrollWidth - track.clientWidth)
    });
  };
  const thumbWidth = 100 / reviews.length;
  const arrowClass = 'flex h-9 w-9 items-center justify-center rounded-control border border-fg/80 text-fg transition-colors duration-150 hover:border-fg hover:bg-fg hover:text-bg disabled:pointer-events-none disabled:opacity-25 focus:outline-none focus-visible:ring-2 focus-visible:ring-fg';
  return <section id="reviews" aria-labelledby="reviews-heading" className="overflow-hidden bg-bg pb-20 pt-0 text-fg md:pb-28" data-id="element-295" data-mp="13dlct4zdww79">
      <div className="px-page" data-id="element-296" data-mp="tgyrkf35c34p">
        <h2 id="reviews-heading" className="sr-only" data-id="element-297" data-mp="ths0nmpkv4q0">
          Testimonials
        </h2>
      </div>

      <ul ref={trackRef} tabIndex={0} aria-label="Client reviews" className="flex snap-x snap-mandatory gap-[var(--gutter)] overflow-x-auto scroll-px-page px-page pb-2 focus:outline-none [&::-webkit-scrollbar]:hidden [scrollbar-width:none]" data-id="element-298" data-mp="yguv6d7xlsjq">
        {reviews.map((review, i) => <li key={review.name} aria-roledescription="slide" aria-label={`${i + 1} of ${reviews.length}`} className="flex w-[78vw] shrink-0 snap-start flex-col rounded-card border border-line bg-surface p-5 sm:w-[46vw] md:w-[calc((100vw-2*var(--page-x)-var(--gutter))/2.2)] md:p-6 lg:w-[calc((100vw-2*var(--page-x)-3*var(--gutter))/3.4)]" data-id="element-299" data-mp="h6qzjfm6iu98" data-mpi={i}>
            <blockquote className="text-[17px] leading-[1.5] text-fg" data-id="element-300" data-mp="zv5ps0ooa48z">“{review.quote}”</blockquote>

            {/* Compact attribution: small headshot, name + company, logo on the right */}
            <div className="mt-auto flex items-center justify-between gap-4 pt-8" data-id="element-301" data-mp="19umtwdoa0a2u">
              <div className="flex min-w-0 items-center gap-3" data-id="element-302" data-mp="14x3db743vl7q">
                <img src={review.photo} alt={review.name} loading="lazy" className="h-10 w-10 shrink-0 object-cover" data-id="element-303" data-mp="3bm9mukrqaf1y" />
                <div className="min-w-0 text-[14px] leading-snug" data-id="element-304" data-mp="3853r9mtd5x1c">
                  <p className="truncate text-fg" data-id="element-305" data-mp="386ye56o73z82">{review.name}</p>
                  <p className="truncate text-fg/50" data-id="element-306" data-mp="3d2dtk31uq36l">{review.company}</p>
                </div>
              </div>
              <span className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[16px] text-fg/70 ${review.markStyle}`} aria-hidden="true" data-id="element-307" data-mp="3i3jj5s07tabw">
                <LogoMark mark={review.mark} data-id="element-308" data-mp="2pfxiosmaalt9" />
                {review.company}
              </span>
            </div>
          </li>)}
      </ul>

      {/* Slider controls: progress rail on the left edge, arrows on the right edge */}
      <div className="mt-6 flex items-center justify-between gap-8 px-page" data-id="element-309" data-mp="342qo96604d89">
        <div role="slider" tabIndex={0} aria-label="Testimonial position" aria-valuemin={1} aria-valuemax={reviews.length} aria-valuenow={current} onPointerDown={e => {
        e.currentTarget.setPointerCapture(e.pointerId);
        scrubTo(e.clientX, e.currentTarget);
      }} onPointerMove={e => {
        if (e.buttons === 1) scrubTo(e.clientX, e.currentTarget);
      }} onKeyDown={e => {
        if (e.key === 'ArrowRight') go(1);
        if (e.key === 'ArrowLeft') go(-1);
      }} className="relative h-6 w-full max-w-[420px] cursor-pointer touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-fg" data-id="element-310" data-mp="344to0hwyhkq6">
          <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" aria-hidden="true" data-id="element-311" data-mp="50fscolrmu7s" />
          <span aria-hidden="true" className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-fg" style={{
          width: `${thumbWidth}%`,
          left: `${progress * (100 - thumbWidth)}%`
        }} data-id="element-312" data-mp="10x53m6h271s" />
        </div>

        <div className="flex shrink-0 gap-2" data-id="element-313" data-mp="3i8itc3yg0djq">
          <button type="button" onClick={() => go(-1)} disabled={atStart} aria-label="Previous review" className={arrowClass} data-id="element-314" data-mp="3i9tz29bg7vdh">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-315" data-mp="38mzs0fgmefu9" />
          </button>
          <button type="button" onClick={() => go(1)} disabled={atEnd} aria-label="Next review" className={arrowClass} data-id="element-316" data-mp="3dlkpw88egnfj">
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-317" data-mp="2zml4d9g6oonl" />
          </button>
        </div>
      </div>
    </section>;
}